import { computed, onBeforeUnmount, reactive, watch, type ComputedRef, type Ref } from 'vue';

// Shared registry for the bottom-right corner of the viewport.
//
// Two unrelated things live there: FloatingBannerStack's cards and the AI chat
// launcher/panel. They used to guess each other's geometry — the stack carried a
// fixed `bottom-24` offset derived from the launcher's size, which was wrong on
// any screen where the launcher isn't mounted and did nothing about the panel
// once expanded. Instead, whatever occupies the corner declares what it takes
// and the stack sits clear of the tallest claim.

/**
 * A claim on the corner: a height in px measured up from the viewport bottom,
 * or 'full' for an occupant covering the whole screen (the chat panel goes
 * full-screen on narrow viewports), where nothing can usefully stack above it.
 */
export type DockClaim = number | 'full';

const claims = reactive(new Map<number, DockClaim>());
let nextClaimId = 0;

const clearance = computed(() => {
  const heights = [...claims.values()].filter((claim): claim is number => claim !== 'full');
  return heights.length > 0 ? Math.max(...heights) : 0;
});

const coversViewport = computed(() => [...claims.values()].includes('full'));

/** For occupants of the corner: publishes a live claim, released on unmount. */
export function useCornerDockClaim(claim: Ref<DockClaim> | ComputedRef<DockClaim>): void {
  const id = nextClaimId++;
  watch(claim, (value) => claims.set(id, value), { immediate: true });
  onBeforeUnmount(() => {
    claims.delete(id);
  });
}

/**
 * For anything that needs to stay out of the corner: `clearance` is how far up
 * to sit, `coversViewport` says there is no room at all.
 */
export function useCornerDock() {
  return { clearance, coversViewport };
}
